import type { Usage } from 'js/src/agent/types';
import { Model } from 'js/src/agent/types';

// List prices per million tokens. OpenAI numbers confirmed against the official docs (Jul 30, 2026).
// Anthropic numbers are list prices via the Messages API; we serve Claude through Vertex so actuals
// may differ. Shared by CostSummary so there is ONE pricing table.
//
// `cachedInput` is the per-million price for cached-prefix READS. OpenAI publishes an explicit
// cached-input price per model (it is NOT a fixed fraction of input — e.g. gpt-4o caches at 0.5x
// while gpt-5.x caches at 0.1x), so set it explicitly where known. When omitted, usageCost() falls
// back to CACHE_READ_MULTIPLIER x input (the Anthropic convention).
interface ModelPricing {
    input: number;
    output: number;
    cachedInput?: number;
}

export const USAGE_PRICING: Record<string, ModelPricing> = {
    // GPT prices confirmed against developers.openai.com/api/docs/pricing (Jul 30, 2026).
    [Model.GPT_4_1]: { input: 2.0, output: 8.0, cachedInput: 0.5 },
    [Model.GPT_4_1_MINI]: { input: 0.4, output: 1.6, cachedInput: 0.1 },
    [Model.GPT_4_1_NANO]: { input: 0.1, output: 0.4, cachedInput: 0.025 },
    [Model.GPT_4O]: { input: 2.5, output: 10.0, cachedInput: 1.25 },
    [Model.GPT_4O_MINI]: { input: 0.15, output: 0.6, cachedInput: 0.075 },
    [Model.GPT_OSS_20B]: { input: 0.1, output: 0.5 },
    [Model.GPT_OSS_120B]: { input: 0.15, output: 0.75 },
    // GPT-5.6 bills at these rates below the 272K-input-token long-context threshold; above it the
    // whole request reprices (Sol 10/45, Terra 4/18, Luna 0.4/1.8). Our context limits keep requests
    // under the threshold, so only the standard rates are modelled here.
    [Model.GPT_5_6_SOL]: { input: 5.0, output: 30.0, cachedInput: 0.5 },
    [Model.GPT_5_6_TERRA]: { input: 2.0, output: 12.0, cachedInput: 0.2 },
    [Model.GPT_5_6_LUNA]: { input: 0.2, output: 1.2, cachedInput: 0.02 },
    [Model.GPT_5_5]: { input: 5.0, output: 30.0, cachedInput: 0.5 },
    [Model.GPT_5_4]: { input: 2.5, output: 15.0, cachedInput: 0.25 },
    [Model.GPT_5_4_MINI]: { input: 0.75, output: 4.5, cachedInput: 0.075 },
    [Model.GPT_5_4_NANO]: { input: 0.2, output: 1.25, cachedInput: 0.02 },
    [Model.GPT_5_2]: { input: 1.75, output: 14.0, cachedInput: 0.175 },
    [Model.GPT_5_1]: { input: 1.25, output: 10.0, cachedInput: 0.125 },
    [Model.GEMINI_3_FLASH_PREVIEW]: { input: 0.5, output: 3.0 },
    [Model.GEMINI_3_5_FLASH]: { input: 1.5, output: 9.0 },
    [Model.GEMINI_3_5_FLASH_LITE]: { input: 0.3, output: 2.5 },
    [Model.GEMINI_3_6_FLASH]: { input: 1.5, output: 7.5 },
    // OpenRouter list prices (openrouter.ai, fetched Aug 12, 2026). OpenRouter bills a flat per-token
    // rate with no cached-input discount — cached reads cost the FULL input price (confirmed against
    // grok-4.6's real billing: ~2.6x our earlier cache-discounted estimate, since ~85% of its input
    // was cached). So cachedInput == input here. For exact figures prefer the response's usage.cost.
    [Model.INKLING_SMALL]: { input: 0.45, output: 1.2, cachedInput: 0.45 },
    [Model.QWEN_3_7_PLUS]: { input: 0.32, output: 1.28, cachedInput: 0.32 },
    [Model.GROK_4_6]: { input: 2.0, output: 6.0, cachedInput: 2.0 },
    [Model.KIMI_K2_6]: { input: 0.58, output: 2.44, cachedInput: 0.58 },
    [Model.CLAUDE_FABLE_5]: { input: 10.0, output: 50.0 },
    [Model.CLAUDE_OPUS_4_8]: { input: 5.0, output: 25.0 },
    [Model.CLAUDE_SONNET_5]: { input: 2.0, output: 10.0 },
    [Model.CLAUDE_SONNET_4_6]: { input: 3.0, output: 15.0 },
    [Model.CLAUDE_HAIKU_4_5]: { input: 1.0, output: 5.0 },
};

// Default to GPT-4.1-mini pricing when the model is unknown.
const DEFAULT_PRICING: ModelPricing = { input: 0.4, output: 1.6 };

// Prompt-cache economics. CACHE_READ_MULTIPLIER is used only when a model has no explicit
// `cachedInput` price: a cached-prefix READ bills at ~0.1x the input price (the Anthropic
// convention). WRITING a cache entry costs a 1.25x premium over normal input — the same rate on
// Anthropic and on OpenAI's gpt-5.6+ models, which is why no model needs an explicit write price.
// (Older OpenAI models never report cache writes, so the multiplier applies to zero tokens there.)
// Anthropic's optional 1-hour TTL doubles that premium instead; the portion written at 1h arrives
// on Usage as cache_creation_1h_input_tokens.
const CACHE_READ_MULTIPLIER = 0.1;
const CACHE_WRITE_MULTIPLIER = 1.25;
const CACHE_WRITE_1H_MULTIPLIER = 2;

// A $ breakdown of one Usage, so a summary can lead with the total and then show how much of it was
// cache-related. Costs are dollars; *Tokens fields are raw token counts (for the hover breakdown).
export interface UsageCost {
    total: number;
    freshInput: number; // uncached input billed at full price
    cacheRead: number; // cached-prefix reads
    cacheWrite: number; // cache writes (1.25x input; Anthropic and gpt-5.6+)
    caching: number; // cacheRead + cacheWrite — the portion of the bill that is cache-related
    output: number;
    saved: number; // vs. paying full input price for the cached-read tokens
    freshInputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
    outputTokens: number;
}

const ZERO_COST: UsageCost = {
    total: 0,
    freshInput: 0,
    cacheRead: 0,
    cacheWrite: 0,
    caching: 0,
    output: 0,
    saved: 0,
    freshInputTokens: 0,
    cacheReadTokens: 0,
    cacheWriteTokens: 0,
    outputTokens: 0,
};

// USAGE_PRICING keys are canonical Model enum values, many of which carry a date suffix
// (e.g. 'gpt-5.4-2026-03-05'), but the model strings we get handed are routinely the short form:
// eval scripts (EVAL_MODEL=gpt-5.4) and stored conversation records both do this. Match those by
// stripping a YYYY-MM-DD suffix from each key, so a short name bills at its real rate instead of
// missing the lookup and silently falling back to DEFAULT_PRICING.
const DATE_SUFFIX = /-\d{4}-\d{2}-\d{2}$/;

// The canonical pricing key for a model string, or undefined when the model has no pricing entry
// (callers that must distinguish "priced" from "fell back to default" check for undefined).
export function resolveModelKey(model?: string): string | undefined {
    if (!model) return undefined;
    if (USAGE_PRICING[model]) return model;
    return Object.keys(USAGE_PRICING).find((key) => key.replace(DATE_SUFFIX, '') === model);
}

export function resolvePricing(model?: string): ModelPricing {
    const key = resolveModelKey(model);
    return key ? USAGE_PRICING[key] : DEFAULT_PRICING;
}

export function usageCost(usage: Usage, model?: string): UsageCost {
    const pricing = resolvePricing(model);
    const per = (tokens: number, price: number) => (tokens / 1_000_000) * price;

    const cacheReadTokens = usage.cache_read_input_tokens ?? 0;
    const cacheWriteTokens = usage.cache_creation_input_tokens ?? 0;
    // input_tokens is normalized to fresh (uncached) input only — see normalizeUsage in LLMClient.
    const freshInputTokens = usage.input_tokens;
    const outputTokens = usage.output_tokens;

    // Prefer the model's explicit cached-input price; fall back to the multiplier when unknown.
    const cachedInputPrice = pricing.cachedInput ?? pricing.input * CACHE_READ_MULTIPLIER;

    // The 1h portion is a subset of cacheWriteTokens, not additional to it. Clamp it so a
    // provider (or a hand-built Usage) reporting more 1h tokens than total writes can't push
    // the 5m remainder negative and silently discount the bill.
    const cacheWrite1hTokens = Math.min(
        usage.cache_creation_1h_input_tokens ?? 0,
        cacheWriteTokens,
    );
    const cacheWrite5mTokens = cacheWriteTokens - cacheWrite1hTokens;

    const freshInput = per(freshInputTokens, pricing.input);
    const cacheRead = per(cacheReadTokens, cachedInputPrice);
    const cacheWrite =
        per(cacheWrite5mTokens, pricing.input * CACHE_WRITE_MULTIPLIER) +
        per(cacheWrite1hTokens, pricing.input * CACHE_WRITE_1H_MULTIPLIER);
    const output = per(outputTokens, pricing.output);
    const caching = cacheRead + cacheWrite;

    return {
        total: freshInput + cacheRead + cacheWrite + output,
        freshInput,
        cacheRead,
        cacheWrite,
        caching,
        output,
        // What the cached-read tokens would have cost at full input price, minus what they actually
        // cost at the cache-read rate.
        saved: per(cacheReadTokens, pricing.input) - cacheRead,
        freshInputTokens,
        cacheReadTokens,
        cacheWriteTokens,
        outputTokens,
    };
}

// Sum per-model breakdowns into one total — for flows that span models (e.g. codeo's Opus
// conversation plus its Sonnet brand analyzer), where a single pricing rate would misprice the
// pieces.
export function sumUsageCost(parts: UsageCost[]): UsageCost {
    return parts.reduce(
        (acc, p) => ({
            total: acc.total + p.total,
            freshInput: acc.freshInput + p.freshInput,
            cacheRead: acc.cacheRead + p.cacheRead,
            cacheWrite: acc.cacheWrite + p.cacheWrite,
            caching: acc.caching + p.caching,
            output: acc.output + p.output,
            saved: acc.saved + p.saved,
            freshInputTokens: acc.freshInputTokens + p.freshInputTokens,
            cacheReadTokens: acc.cacheReadTokens + p.cacheReadTokens,
            cacheWriteTokens: acc.cacheWriteTokens + p.cacheWriteTokens,
            outputTokens: acc.outputTokens + p.outputTokens,
        }),
        ZERO_COST,
    );
}

export function formatUsd(cost: number): string {
    if (cost <= 0) return '$0';
    if (cost < 0.001) return '<$0.001';
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(3)}`;
}
